/**
 * AI チャットの API サービス（/api/chat/*）
 *
 * 会話の履歴はサーバーが持つ。送るのは会話 ID と今回の発言だけ。
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { extractErrorCode } from '@/services/apiErrors';
import type { components } from '@/types/api-schema';

export type ChatEntry = components['schemas']['ChatEntry'];
export type ChatPendingAction = components['schemas']['ChatPendingAction'];
export type ChatUsage = components['schemas']['ChatUsage'];
export type ChatMessageRequest = components['schemas']['ChatMessageRequest'];
export type ChatMessageResponse = components['schemas']['ChatMessageResponse'];
export type ChatConversationResponse = components['schemas']['ChatConversationResponse'];
export type ChatActionResult = components['schemas']['ChatActionResult'];
export type ChatHomeResponse = components['schemas']['ChatHomeResponse'];
export type ChatFlowButton = components['schemas']['ChatFlowButton'];
export type ChatFlowStep = components['schemas']['ChatFlowStep'];
export type ChatHomeEvent = components['schemas']['ChatHomeEvent'];
export type ChatHomeShortcut = components['schemas']['ChatHomeShortcut'];
export type ChatFlowRunRequest = components['schemas']['ChatFlowRunRequest'];
export type ChatFlowRunResponse = components['schemas']['ChatFlowRunResponse'];
export type ChatFollowUp = components['schemas']['ChatFollowUp'];
export type ChatResultBlock = components['schemas']['ChatResultBlock'];
export type ChatEntryAction = components['schemas']['ChatEntryAction'];
export type ChatCommentOption = components['schemas']['ChatCommentOption'];
export type ChatUsageBreakdownResponse = components['schemas']['ChatUsageBreakdownResponse'];
export type ChatConversationSummary = components['schemas']['ChatConversationSummary'];
export type ChatEntryPoint = ChatFlowRunRequest['entry'];
export type ChatFeedbackKind = components['schemas']['ChatFeedbackRequest']['kind'];

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/** チャット API のエラー。画面で分岐するために status と code を持つ */
export class ChatApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ChatApiError';
    this.status = status;
    this.code = code;
  }
}

async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new ChatApiError('ログインが必要です', 401);
  }
  return token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const code = extractErrorCode(body);
    const message =
      (body && typeof body.message === 'string' && body.message) ||
      (response.status === 429
        ? 'リクエストが集中しています。しばらくしてから再度お試しください。'
        : 'AIチャットでエラーが発生しました。もう一度お試しください。');
    throw new ChatApiError(message, response.status, code);
  }
  return (await response.json()) as T;
}

export function sendChatMessage(body: ChatMessageRequest): Promise<ChatMessageResponse> {
  return request<ChatMessageResponse>('/api/chat/messages', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getChatConversation(conversationId: string): Promise<ChatConversationResponse> {
  return request<ChatConversationResponse>(
    `/api/chat/conversations/${encodeURIComponent(conversationId)}`,
  );
}

/** これまでの会話（30 日分。新しい順） */
export async function listChatConversations(): Promise<ChatConversationSummary[]> {
  const response = await request<components['schemas']['ChatConversationListResponse']>('/api/chat/conversations');
  return response.conversations ?? [];
}

export function getChatUsage(): Promise<ChatUsage> {
  return request<ChatUsage>('/api/chat/usage');
}

export function confirmChatAction(
  actionId: string,
  acknowledgeNotification: boolean,
): Promise<ChatActionResult> {
  return request<ChatActionResult>(
    `/api/chat/actions/${encodeURIComponent(actionId)}/confirm`,
    { method: 'POST', body: JSON.stringify({ acknowledgeNotification }) },
  );
}

export function cancelChatAction(actionId: string): Promise<ChatActionResult> {
  return request<ChatActionResult>(
    `/api/chat/actions/${encodeURIComponent(actionId)}/cancel`,
    { method: 'POST', body: '{}' },
  );
}

export function getChatHome(
  contextEventId: string | null,
  entry: ChatEntryPoint,
): Promise<ChatHomeResponse> {
  const params = new URLSearchParams({ entry: entry ?? 'other' });
  if (contextEventId) params.set('contextEventId', contextEventId);
  return request<ChatHomeResponse>(`/api/chat/home?${params.toString()}`);
}

/** 用途の実行の入力（既定値のある項目は省略できる） */
export type ChatFlowRunInput = Partial<ChatFlowRunRequest> & {
  slots: NonNullable<ChatFlowRunRequest['slots']>;
};

export function runChatFlow(flowId: string, input: ChatFlowRunInput): Promise<ChatFlowRunResponse> {
  const body: ChatFlowRunRequest = {
    conversationId: null,
    action: null,
    target: null,
    entry: 'other',
    parentConversationId: null,
    ...input,
  };
  return request<ChatFlowRunResponse>(`/api/chat/flows/${encodeURIComponent(flowId)}/run`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getChatComments(eventId: string): Promise<ChatCommentOption[]> {
  const response = await request<components['schemas']['ChatCommentOptionsResponse']>(
    `/api/chat/flows/reply_comment/comments?eventId=${encodeURIComponent(eventId)}`,
  );
  return response.comments ?? [];
}

export function getChatUsageBreakdown(): Promise<ChatUsageBreakdownResponse> {
  return request<ChatUsageBreakdownResponse>('/api/chat/usage/breakdown');
}

/** コピー・👍👎・次の一手を記録する（失敗しても画面は止めない） */
export function sendChatFeedback(conversationId: string, kind: ChatFeedbackKind): void {
  void request(`/api/chat/conversations/${encodeURIComponent(conversationId)}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  }).catch(() => undefined);
}

/**
 * 段の途中でやめた・使い方の答えを見たことを記録する。
 * パネルを閉じる直前にも送れるよう keepalive を付ける。失敗しても画面は止めない。
 */
export function sendChatClientEvent(
  input: Pick<components['schemas']['ChatClientEventRequest'], 'kind' | 'flowId'> &
    Partial<components['schemas']['ChatClientEventRequest']>,
): void {
  const body: components['schemas']['ChatClientEventRequest'] = { step: null, item: null, ...input };
  void request('/api/chat/events', {
    method: 'POST',
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => undefined);
}
